import { UzBadge, UzButton, UzCard, UzConfirmDialog, UzSpinner } from '@utazzo/ui';
import {
  Ban,
  CheckCircle2,
  Clock,
  Copy,
  Download,
  Eye,
  FileSignature,
  PenLine,
  Send,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';

import {
  useCopySigningLink,
  useDownloadContract,
  useLeadContracts,
  useResendContract,
  useVoidContract,
} from '../use-contracts';

import type { ContractListItem, ContractStatus } from '@utazzo/shared';

/**
 * Contracts tab on the lead detail page: one card per contract with a
 * signing-status timeline and resend / download / void actions. Contracts are
 * auto-sent when the first instalment is paid; this tab is the operator's
 * window into (and manual lever over) that flow.
 */

const STATUS_META: Record<
  ContractStatus,
  { label: string; tone: 'default' | 'secondary' | 'success' | 'warning' | 'destructive' }
> = {
  DRAFT: { label: 'Draft', tone: 'secondary' },
  SENT: { label: 'Sent - awaiting customer', tone: 'default' },
  VIEWED: { label: 'Viewed by customer', tone: 'warning' },
  CUSTOMER_SIGNED: { label: 'Customer signed - awaiting Utazzo', tone: 'warning' },
  COMPLETED: { label: 'Completed', tone: 'success' },
  DECLINED: { label: 'Declined', tone: 'destructive' },
  VOIDED: { label: 'Voided', tone: 'secondary' },
};

const RESENDABLE: ContractStatus[] = ['SENT', 'VIEWED', 'CUSTOMER_SIGNED'];
const VOIDABLE: ContractStatus[] = ['DRAFT', 'SENT', 'VIEWED', 'CUSTOMER_SIGNED', 'DECLINED'];

interface TimelineStep {
  key: string;
  label: string;
  detail?: string;
  at: Date | null;
  icon: React.ComponentType<{ className?: string }>;
  done: boolean;
  failed?: boolean;
}

const fmt = (d: Date): string =>
  d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const buildTimeline = (c: ContractListItem): TimelineStep[] => {
  const steps: TimelineStep[] = [
    {
      key: 'created',
      label: 'Contract generated',
      at: c.createdAt,
      icon: FileSignature,
      done: true,
    },
    {
      key: 'sent',
      label: `Sent to ${c.customerSignerName}`,
      detail: c.customerSignerEmail,
      at: c.sentAt,
      icon: Send,
      done: Boolean(c.sentAt),
    },
    {
      key: 'viewed',
      label: 'Opened by customer',
      at: c.viewedAt,
      icon: Eye,
      done: Boolean(c.viewedAt),
    },
    {
      key: 'customer-signed',
      label: 'Customer signed',
      at: c.customerSignedAt,
      icon: PenLine,
      done: Boolean(c.customerSignedAt),
    },
    {
      key: 'utazzo-signed',
      label: `Countersigned by ${c.utazzoSignerName}`,
      detail: c.utazzoSignerEmail,
      at: c.utazzoSignedAt,
      icon: PenLine,
      done: Boolean(c.utazzoSignedAt),
    },
    {
      key: 'completed',
      label: 'Completed - signed PDF archived',
      at: c.completedAt,
      icon: CheckCircle2,
      done: Boolean(c.completedAt),
    },
  ];
  if (c.status === 'DECLINED') {
    return [
      ...steps.filter((s) => s.done),
      {
        key: 'declined',
        label: 'Declined',
        detail: c.declineReason ?? undefined,
        at: c.declinedAt,
        icon: XCircle,
        done: true,
        failed: true,
      },
    ];
  }
  if (c.status === 'VOIDED') {
    return [
      ...steps.filter((s) => s.done),
      { key: 'voided', label: 'Voided by staff', at: null, icon: Ban, done: true, failed: true },
    ];
  }
  return steps;
};

const ContractTimeline = ({ contract }: { contract: ContractListItem }): React.JSX.Element => (
  <ol className="space-y-3">
    {buildTimeline(contract).map((step) => {
      const Icon = step.done ? step.icon : Clock;
      return (
        <li key={step.key} className="flex gap-3">
          <div
            className={
              step.failed
                ? 'mt-0.5 text-red-500'
                : step.done
                  ? 'mt-0.5 text-emerald-600'
                  : 'mt-0.5 text-zinc-300'
            }
          >
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div
              className={step.done ? 'text-sm font-medium text-zinc-900' : 'text-sm text-zinc-400'}
            >
              {step.label}
            </div>
            {step.detail ? <div className="text-xs text-zinc-500">{step.detail}</div> : null}
            <div className="text-xs text-zinc-400">
              {step.at ? fmt(step.at) : step.done ? '' : 'Pending'}
            </div>
          </div>
        </li>
      );
    })}
  </ol>
);

const ContractCard = ({
  contract,
  leadId,
}: {
  contract: ContractListItem;
  leadId: string;
}): React.JSX.Element => {
  const meta = STATUS_META[contract.status];
  const resend = useResendContract(leadId);
  const copyLink = useCopySigningLink(leadId);
  const voidContract = useVoidContract(leadId);
  const download = useDownloadContract();
  const [confirmVoid, setConfirmVoid] = useState(false);

  return (
    <UzCard title={contract.title} actions={<UzBadge tone={meta.tone}>{meta.label}</UzBadge>}>
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <ContractTimeline contract={contract} />
        <div className="flex shrink-0 flex-col gap-2">
          {RESENDABLE.includes(contract.status) ? (
            <UzButton
              size="sm"
              variant="outline"
              loading={resend.isPending}
              onClick={() => resend.mutate(contract.id)}
            >
              <Send className="mr-1.5 h-3.5 w-3.5" /> Resend invitation
            </UzButton>
          ) : null}
          {/* Customer's turn only - the link is checked / renewed before it is copied. */}
          {contract.status === 'SENT' || contract.status === 'VIEWED' ? (
            <UzButton
              size="sm"
              variant="outline"
              loading={copyLink.isPending}
              onClick={() => copyLink.mutate(contract.id)}
            >
              <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy link
            </UzButton>
          ) : null}
          <UzButton
            size="sm"
            variant="outline"
            loading={download.isPending}
            onClick={() => download.mutate(contract.id)}
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            {contract.signedPdfKey ? 'Download signed PDF' : 'Download PDF'}
          </UzButton>
          {VOIDABLE.includes(contract.status) ? (
            <UzButton
              size="sm"
              variant="outline"
              className="text-red-600"
              onClick={() => setConfirmVoid(true)}
            >
              <Ban className="mr-1.5 h-3.5 w-3.5" /> Void
            </UzButton>
          ) : null}
        </div>
      </div>
      <UzConfirmDialog
        open={confirmVoid}
        onOpenChange={setConfirmVoid}
        title="Void this contract?"
        description="The signing links stop working and the document is withdrawn from Documenso. A fresh contract can be generated afterwards."
        confirmLabel="Void contract"
        tone="destructive"
        onConfirm={() => voidContract.mutateAsync(contract.id).then(() => undefined)}
      />
    </UzCard>
  );
};

export const LeadContractsTab = ({ leadId }: { leadId: string }): React.JSX.Element => {
  const query = useLeadContracts(leadId);

  if (query.isLoading) {
    return (
      <UzCard title="Contracts">
        <div className="flex items-center gap-2 py-4 text-sm text-zinc-500">
          <UzSpinner size="sm" /> Loading contracts…
        </div>
      </UzCard>
    );
  }

  const contracts = query.data ?? [];
  if (contracts.length === 0) {
    return (
      <UzCard title="Contracts">
        <p className="py-4 text-sm text-zinc-500">
          No contracts yet. The holiday booking contract is generated and emailed for signature
          automatically as soon as the customer pays the first instalment (or the full amount).
        </p>
      </UzCard>
    );
  }

  return (
    <div className="space-y-4">
      {contracts.map((c) => (
        <ContractCard key={c.id} contract={c} leadId={leadId} />
      ))}
    </div>
  );
};
